import React, { useState } from "react";
import { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";
import { PlusIcon } from "lucide-react";
import { postFetcher } from "@/lib/swr";
import { useApps } from "@/hooks/apps";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { GROUPS } from "@/constants/app-groups";
import { GroupCombobox } from "./group-combobox";
import { refreshApps } from "./refresh-apps";

const APP_KEY_RE = /^[A-Z][A-Z0-9]{1,5}$/;

// New app dialog. After the old flat /apps management page was removed, the create entry point is consolidated to the "+" next to "Apps" in the sidebar.
export const CreateAppDialog = () => {
  const { toast } = useToast();
  const { mutate } = useSWRConfig();
  const apps = useApps();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [prdKey, setPrdKey] = useState("");
  const [group, setGroup] = useState("");
  const key = prdKey.trim();
  const keyValid = APP_KEY_RE.test(key);
  const keyError = prdKey && !keyValid
    ? "Use 2–6 letters/digits, starting with a letter."
    : null;

  // Extensible options: canonical GROUPS ∪ groups already in use.
  const groupOptions = [
    ...new Set([
      ...apps.map((a) => a.group).filter((g): g is string => !!g),
      ...GROUPS,
    ]),
  ].sort();

  const { trigger, isMutating } = useSWRMutation("/apps", postFetcher, {
    onSuccess: () => {
      refreshApps(mutate);
      toast({ title: "App created" });
      setName("");
      setPrdKey("");
      setGroup("");
      setOpen(false);
    },
    onError: (err: unknown) =>
      toastError(toast, "Create failed", err),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Add app"
          title="Add app"
          className="h-8 w-8 text-muted-foreground hover:text-foreground"
        >
          <PlusIcon className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New App</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* id/code 由服务端递增分配(id=code 不变量),不再让人填 */}
          <label className="block space-y-1">
            <span className="text-sm text-muted-foreground">Name</span>
            <Input
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
              className="h-8"
            />
          </label>
          <div className="space-y-1">
            <label htmlFor="create-app-key" className="text-sm text-muted-foreground">
              PRD key
            </label>
            <Input
              id="create-app-key"
              value={prdKey}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPrdKey(e.target.value.toUpperCase())
              }
              placeholder="e.g. WED"
              required
              aria-invalid={!!keyError}
              aria-describedby={keyError
                ? "create-app-key-help create-app-key-error"
                : "create-app-key-help"}
              className="font-mono"
            />
            <span id="create-app-key-help" className="block text-xs text-muted-foreground">
              Required. 2–6 letters/digits, starting with a letter; unique.
              Short IDs use this prefix, e.g. {keyValid ? key : "WED"}-160.
            </span>
            {keyError && (
              <span id="create-app-key-error" role="alert" className="block text-xs text-destructive">
                {keyError}
              </span>
            )}
          </div>
          <div className="space-y-1">
            <span className="text-sm text-muted-foreground">Group</span>
            <GroupCombobox
              value={group}
              onChange={setGroup}
              options={groupOptions}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={() => trigger(
              { name, key, group: group.trim() },
              { throwOnError: false },
            )}
            disabled={isMutating || !name.trim() || !keyValid}
          >
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
